import { useMemo } from 'react';
import { ForecastCalculator } from '../utils/forecastCalculations';

/**
 * Custom hook for calculating plan totals with enhanced variance tracking
 * Preserves all existing plan totals calculation logic
 */
export const usePlanTotals = (filteredOutcomes, selectedDept = null) => {
  const calculator = useMemo(() => new ForecastCalculator(), []);

  const planTotals = useMemo(() => {
    if (!filteredOutcomes || filteredOutcomes.length === 0) {
      return {
        totalApprovedCapex: 0,
        totalApprovedOpex: 0,
        totalApprovedBudget: 0,
        totalForecastCapex: 0,
        totalForecastOpex: 0,
        totalForecastBudget: 0,
        totalOfficialForecastCapex: 0,
        totalOfficialForecastOpex: 0,
        totalOfficialForecastBudget: 0,
        totalActualCapex: 0,
        totalActualOpex: 0,
        totalActualBudget: 0,
        totalStaffForecastCapex: 0,
        totalStaffForecastOpex: 0,
        totalStaffForecastBudget: 0,
        totalStaffActualCapex: 0,
        totalStaffActualOpex: 0,
        totalStaffActualBudget: 0,
        totalFixedForecastCapex: 0,
        totalFixedForecastOpex: 0,
        totalFixedForecastBudget: 0,
        totalFixedActualCapex: 0,
        totalFixedActualOpex: 0,
        totalFixedActualBudget: 0,
        totalBudgetVariance: 0,
        totalExecutionVariance: 0,
        totalForecastVariance: 0,
        eoyLandingEstimate: 0,
        ppmEoyLandingEstimate: 0,
        outcomesWithApprovedBudget: 0,
        outcomesWithProjectedForecast: 0,
        outcomesWithOfficialForecast: 0,
        outcomesWithActualSpend: 0,
        outcomesOverBudget: 0,
        outcomesOverTarget: 0,
        ytdActualBudget: 0,
        ytdProjectedBudget: 0,
        ytdOfficialForecastBudget: 0,
        ytdCutoffMonth: -1,
        planMonthlyActual: new Array(12).fill(0),
        planMonthlyProjected: new Array(12).fill(0),
        planMonthlyOfficialForecast: new Array(12).fill(0),
      };
    }

    return calculator.calculatePlanTotals(filteredOutcomes, selectedDept);
  }, [filteredOutcomes, calculator, selectedDept]);

  /**
   * Enhanced status assessment for the entire plan
   * PRESERVES ALL EXISTING MULTI-TIER LOGIC
   */
  const planStatusAssessment = useMemo(() => {
    const hasOfficialForecast = planTotals.totalOfficialForecastBudget > 0;
    const hasActualSpend = planTotals.totalActualBudget > 0;
    
    // Priority 1: Budget Compliance (Projected vs Approved)
    const budgetVariance = planTotals.totalForecastBudget - planTotals.totalApprovedBudget;
    const budgetVariancePercent = planTotals.totalApprovedBudget > 0 ? 
      (budgetVariance / planTotals.totalApprovedBudget) * 100 : 0;
    
    // Priority 2: Execution Performance (Actual vs Baseline Forecast)
    const baselineForecast = hasOfficialForecast ? 
      planTotals.totalOfficialForecastBudget : planTotals.totalForecastBudget;
    const executionVariance = planTotals.totalActualBudget - baselineForecast;
    const executionVariancePercent = baselineForecast > 0 ? 
      (executionVariance / baselineForecast) * 100 : 0;
    
    // Priority 3: Official vs Projected Variance (if both exist)
    const forecastVariance = hasOfficialForecast ? 
      planTotals.totalOfficialForecastBudget - planTotals.totalForecastBudget : 0;
    const forecastVariancePercent = planTotals.totalForecastBudget > 0 ? 
      (forecastVariance / planTotals.totalForecastBudget) * 100 : 0;
    
    let status = 'On Target';
    let statusColor = 'success.main';
    let statusIcon = '🎯';
    let primaryVariance = 0;
    let primaryVariancePercent = 0;
    let statusDetail = '';
    
    // First Priority: Budget Compliance
    if (planTotals.totalApprovedBudget > 0 && Math.abs(budgetVariancePercent) > 5) {
      if (budgetVariancePercent > 5) {
        status = 'Over Budget';
        statusColor = 'error.main';
        statusIcon = '🚨';
        primaryVariance = budgetVariance;
        primaryVariancePercent = budgetVariancePercent;
        statusDetail = 'Projected exceeds approved budget';
      } else if (budgetVariancePercent < -5) {
        status = 'Under Budget';
        statusColor = 'success.main';
        statusIcon = '💰';
        primaryVariance = budgetVariance;
        primaryVariancePercent = budgetVariancePercent;
        statusDetail = 'Projected under approved budget';
      }
    }
    // Second Priority: Execution Performance (only if budget compliant)
    else if (hasActualSpend && Math.abs(executionVariancePercent) > 10) {
      if (executionVariancePercent > 10) {
        status = 'Over Target';
        statusColor = 'error.main';
        statusIcon = '⚠️';
        primaryVariance = executionVariance;
        primaryVariancePercent = executionVariancePercent;
        statusDetail = hasOfficialForecast ? 'Actual exceeds official forecast' : 'Actual exceeds projected forecast';
      } else if (executionVariancePercent < -10) {
        status = 'Under Target';
        statusColor = 'warning.main';
        statusIcon = '📉';
        primaryVariance = executionVariance;
        primaryVariancePercent = executionVariancePercent;
        statusDetail = hasOfficialForecast ? 'Actual under official forecast' : 'Actual under projected forecast';
      }
    }
    // Third Priority: Forecast Alignment (if no major budget or execution issues)
    else if (hasOfficialForecast && Math.abs(forecastVariancePercent) > 15) {
      if (forecastVariancePercent > 0) {
        status = 'Forecast Gap';
        statusColor = 'warning.main';
        statusIcon = '📊';
        primaryVariance = forecastVariance;
        primaryVariancePercent = forecastVariancePercent;
        statusDetail = 'Official forecast exceeds projected';
      } else {
        status = 'Forecast Gap';
        statusColor = 'info.main';
        statusIcon = '📊';
        primaryVariance = forecastVariance;
        primaryVariancePercent = forecastVariancePercent;
        statusDetail = 'Projected exceeds official forecast';
      }
    }
    // Default: On Target
    else {
      primaryVariance = hasActualSpend ? executionVariance : budgetVariance;
      primaryVariancePercent = hasActualSpend ? executionVariancePercent : budgetVariancePercent;
      statusDetail = hasActualSpend ? 
        (hasOfficialForecast ? 'Tracking official forecast' : 'Tracking projected forecast') :
        'Within approved budget';
    }
    
    return {
      status,
      statusColor,
      statusIcon,
      primaryVariance,
      primaryVariancePercent,
      statusDetail,
      budgetVariance,
      budgetVariancePercent,
      executionVariance,
      executionVariancePercent,
      forecastVariance,
      forecastVariancePercent,
      hasOfficialForecast,
      hasActualSpend,
      baselineForecast
    };
  }, [planTotals]);

  /**
   * Data quality metrics for the plan
   */
  const dataQualityMetrics = useMemo(() => {
    if (!filteredOutcomes || filteredOutcomes.length === 0) {
      return {
        totalOutcomes: 0,
        outcomesWithStaffingData: 0,
        outcomesWithCompanyData: 0,
        outcomesWithFixedCosts: 0,
        outcomesWithUploadedSpendData: 0,
        outcomesWithOfficialForecast: 0,
        staffingDataPercentage: 0,
        companyDataPercentage: 0,
        actualSpendPercentage: 0,
        officialForecastPercentage: 0
      };
    }

    const totalOutcomes = filteredOutcomes.length;
    const outcomesWithStaffingData = filteredOutcomes.filter(o => o.usingStaffingData).length;
    const outcomesWithCompanyData = filteredOutcomes.filter(o => Object.keys(o.companyBreakdown || {}).length > 0).length;
    const outcomesWithFixedCosts = filteredOutcomes.filter(o => Object.keys(o.fixedCostBreakdown || {}).length > 0).length;
    const outcomesWithUploadedSpendData = filteredOutcomes.filter(o => o.hasUploadedSpendData).length;
    const outcomesWithOfficialForecast = filteredOutcomes.filter(o => o.hasOfficialForecast).length;

    return {
      totalOutcomes,
      outcomesWithStaffingData,
      outcomesWithCompanyData,
      outcomesWithFixedCosts,
      outcomesWithUploadedSpendData,
      outcomesWithOfficialForecast,
      staffingDataPercentage: totalOutcomes > 0 ? (outcomesWithStaffingData / totalOutcomes) * 100 : 0,
      companyDataPercentage: totalOutcomes > 0 ? (outcomesWithCompanyData / totalOutcomes) * 100 : 0,
      actualSpendPercentage: totalOutcomes > 0 ? (outcomesWithUploadedSpendData / totalOutcomes) * 100 : 0,
      officialForecastPercentage: totalOutcomes > 0 ? (outcomesWithOfficialForecast / totalOutcomes) * 100 : 0
    };
  }, [filteredOutcomes]);

  /**
   * Performance metrics for monitoring
   */
  const performanceMetrics = useMemo(() => {
    if (!filteredOutcomes || filteredOutcomes.length === 0) {
      return {
        totalOutcomes: 0,
        averageCompaniesPerOutcome: 0,
        averageFixedCostsPerOutcome: 0,
        complexOutcomes: 0,
        simpleOutcomes: 0
      };
    }

    const totalOutcomes = filteredOutcomes.length;
    const totalCompanies = filteredOutcomes.reduce((sum, o) => sum + Object.keys(o.companyBreakdown || {}).length, 0);
    const totalFixedCosts = filteredOutcomes.reduce((sum, o) => sum + Object.keys(o.fixedCostBreakdown || {}).length, 0);
    
    // Complex outcomes have multiple companies or fixed costs
    const complexOutcomes = filteredOutcomes.filter(o => 
      Object.keys(o.companyBreakdown || {}).length > 1 || 
      Object.keys(o.fixedCostBreakdown || {}).length > 0
    ).length;
    
    const simpleOutcomes = totalOutcomes - complexOutcomes;

    return {
      totalOutcomes,
      averageCompaniesPerOutcome: totalOutcomes > 0 ? totalCompanies / totalOutcomes : 0,
      averageFixedCostsPerOutcome: totalOutcomes > 0 ? totalFixedCosts / totalOutcomes : 0,
      complexOutcomes,
      simpleOutcomes,
      complexityPercentage: totalOutcomes > 0 ? (complexOutcomes / totalOutcomes) * 100 : 0
    };
  }, [filteredOutcomes]);

  return {
    planTotals,
    planStatusAssessment,
    dataQualityMetrics,
    performanceMetrics
  };
};